import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PublicFooter from "@/components/PublicFooter";
import { COMPANY, LEGAL_EFFECTIVE_DATE, LEGAL_VERSION } from "@/lib/legalVersion";

// Phase P.18 — GDPR — Data Processing Addendum (DPA).
// Standard EU controller-to-processor Article 28 addendum. Automatically
// applies to every JobsBook.app tenant as part of the Terms of Service.
// Written to be shippable and lawyer-review quality — not a placeholder.

const clauses = [
  {
    n: 1, title: "Definitions",
    body: (
      <>
        <p>Capitalised terms not defined here have the meaning given in the GDPR:</p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li><strong>"Controller"</strong>, <strong>"Processor"</strong>, <strong>"Data Subject"</strong>, <strong>"Personal Data"</strong>, <strong>"Processing"</strong>, <strong>"Personal Data Breach"</strong> and <strong>"Supervisory Authority"</strong> have the meaning in Article 4 GDPR.</li>
          <li><strong>"Customer"</strong> means the tenant / subscriber to the Service.</li>
          <li><strong>"Provider"</strong> means {COMPANY.legal_name}.</li>
          <li><strong>"Customer Personal Data"</strong> means Personal Data uploaded to or generated by the Service under the Customer's account.</li>
          <li><strong>"Sub-processor"</strong> means a third party engaged by the Provider to process Customer Personal Data.</li>
        </ul>
      </>
    ),
  },
  {
    n: 2, title: "Roles",
    body: (
      <p>
        The parties acknowledge that in respect of Customer Personal Data uploaded into the
        Service (e.g. customer records, staff records, bookings, invoices), the Customer is the
        <strong> Controller</strong> and the Provider is the <strong>Processor</strong>. Where the
        Provider processes Personal Data of the Customer's own account holder for its own
        purposes (billing, account administration, security), the Provider is an independent
        Controller and its processing is described in the Privacy Policy.
      </p>
    ),
  },
  {
    n: 3, title: "Subject-matter, duration and nature of processing",
    body: (
      <>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Subject-matter:</strong> provision of the JobsBook.app Service (bookings, staff dispatch, invoicing, customer records).</li>
          <li><strong>Duration:</strong> for as long as the Customer's subscription is active, plus the retention periods set out in the Privacy Policy.</li>
          <li><strong>Nature and purpose:</strong> hosting, storing, transmitting, displaying and generating personal-data-containing artefacts required to deliver the Service.</li>
          <li><strong>Categories of Data Subjects:</strong> Customer's own employees / staff, Customer's clients / consumers, Customer's account users.</li>
          <li><strong>Categories of Personal Data:</strong> identification data (name, contact details), professional data (job role, staff PIN), commercial data (invoices, bookings), technical data (IP address truncated, session tokens), address / geolocation data.</li>
        </ul>
      </>
    ),
  },
  {
    n: 4, title: "Provider obligations",
    body: (
      <>
        <p>The Provider shall:</p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>process Customer Personal Data only on documented instructions from the Customer, including transfers, unless required by EU or Member State law;</li>
          <li>ensure that persons authorised to process Personal Data have committed themselves to confidentiality;</li>
          <li>implement the technical and organisational security measures described in Annex II (below);</li>
          <li>assist the Customer, taking into account the nature of processing, in responding to Data Subject rights requests, and in complying with Articles 32–36 GDPR;</li>
          <li>notify the Customer without undue delay after becoming aware of a Personal Data Breach;</li>
          <li>at the Customer's choice, delete or return all Personal Data at end of provision, and delete existing copies unless retention is required by law;</li>
          <li>make available to the Customer all information necessary to demonstrate compliance with Article 28 GDPR and allow for audits, including inspections, conducted by the Customer or a mandated auditor (subject to reasonable confidentiality and scheduling arrangements).</li>
        </ul>
      </>
    ),
  },
  {
    n: 5, title: "Sub-processors",
    body: (
      <>
        <p>
          The Customer grants a general written authorisation to the Provider to engage
          Sub-processors, subject to (a) written contractual terms flowing down GDPR-equivalent
          obligations, (b) the Provider remaining fully liable to the Customer for the
          Sub-processor's acts and omissions, and (c) the Provider giving the Customer at least
          30 days' prior written notice (by email or in-app) of any intended addition or
          replacement of a Sub-processor. The Customer may object on reasonable data-protection
          grounds; in that case the parties will discuss the matter in good faith and, failing
          resolution, the Customer may terminate the affected part of the Service.
        </p>
        <p className="mt-2">
          The current list of Sub-processors is published in the Privacy Policy, Section 4.
        </p>
      </>
    ),
  },
  {
    n: 6, title: "International transfers",
    body: (
      <p>
        Where the Provider transfers Customer Personal Data outside the EEA, the transfer is
        governed by the European Commission's Standard Contractual Clauses (SCCs) — Decision
        (EU) 2021/914 — as incorporated by reference into this DPA, Module 2 or Module 3 as
        applicable. The Provider will maintain supplementary technical measures (encryption in
        transit, minimum-necessary payloads) commensurate with the risk identified in the
        transfer impact assessment.
      </p>
    ),
  },
  {
    n: 7, title: "Data Subject rights",
    body: (
      <p>
        Taking into account the nature of the processing, the Provider shall assist the Customer
        by appropriate technical and organisational measures, insofar as this is possible, in
        fulfilling the Customer's obligation to respond to requests to exercise Data Subject
        rights (Articles 12–23 GDPR). The Customer can perform most access, export, rectification
        and erasure operations self-serve via the Service.
      </p>
    ),
  },
  {
    n: 8, title: "Personal Data Breach",
    body: (
      <p>
        The Provider will notify the Customer without undue delay — and, where feasible, within
        72 hours — after becoming aware of a Personal Data Breach affecting Customer Personal
        Data. The notice will describe the nature of the breach, the categories and approximate
        number of Data Subjects and records affected, the likely consequences, and the measures
        taken or proposed to address it.
      </p>
    ),
  },
  {
    n: 9, title: "Deletion and return at end of processing",
    body: (
      <p>
        On termination of the Service, the Provider will make Customer Personal Data available
        for export for at least 30 days (via <em>Settings → Privacy &amp; data → Export my data</em>)
        after which it will be permanently deleted, except for invoice / ledger records which
        will be anonymised and retained for the 7-year period required by Irish Revenue.
      </p>
    ),
  },
  {
    n: 10, title: "Liability and governing law",
    body: (
      <p>
        The liability and governing law provisions of the main Terms of Service apply to this
        DPA. In case of conflict between this DPA and the Terms, this DPA prevails with respect
        to the subject-matter of Article 28 GDPR.
      </p>
    ),
  },
  {
    n: 11, title: "Annex I — Description of processing",
    body: (
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Data exporter:</strong> the Customer (Controller).</li>
        <li><strong>Data importer:</strong> {COMPANY.legal_name} (Processor).</li>
        <li><strong>Frequency of transfer:</strong> continuous.</li>
        <li><strong>Retention:</strong> as described in the Privacy Policy, Section 6.</li>
        <li><strong>Purpose:</strong> to deliver the JobsBook.app Service to the Customer.</li>
      </ul>
    ),
  },
  {
    n: 12, title: "Annex II — Technical and organisational measures",
    body: (
      <>
        <p>The Provider maintains the following minimum measures:</p>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>TLS 1.2+ encryption in transit; HSTS enforced on production.</li>
          <li>Encryption at rest for the primary database (AES-256).</li>
          <li>Bcrypt password &amp; PIN hashing (cost ≥ 12).</li>
          <li>Multi-tenant isolation enforced at the database driver level.</li>
          <li>Short-lived (24 h) JWT-based sessions with server-side revocation on credential change.</li>
          <li>Principle of least privilege for production access; MFA enforced for on-call engineers.</li>
          <li>Continuous dependency scanning and prompt patching of critical CVEs.</li>
          <li>Encrypted rolling backups (30-day retention).</li>
          <li>Written incident-response process with named responder rotations.</li>
        </ul>
      </>
    ),
  },
];

export default function Dpa() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="dpa-page">
      <div className="border-b border-border">
        <div className="max-w-[820px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-bold" data-testid="dpa-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to JobsBook
          </Link>
          <span className="mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Legal</span>
        </div>
      </div>

      <main className="max-w-[820px] mx-auto px-5 md:px-8 py-12">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">// {COMPANY.trading_name}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Data Processing Addendum</h1>
        <div className="mt-2 text-sm text-muted-foreground">
          Effective: <span className="mono">{LEGAL_EFFECTIVE_DATE}</span>{" "}
          · Version <span className="mono">{LEGAL_VERSION}</span>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-foreground/80">
          This Data Processing Addendum ("DPA") supplements the JobsBook.app Terms of Service.
          It reflects the parties' obligations under Article 28 of the General Data Protection
          Regulation (Regulation (EU) 2016/679) where you upload personal data of your own
          customers into JobsBook.app. By accepting the Terms you accept this DPA. An executed
          copy is available on request from {COMPANY.contact_email}.
        </p>

        <div className="mt-8 space-y-8">
          {clauses.map((s) => (
            <section key={s.n} data-testid={`dpa-section-${s.n}`}>
              <h2 className="text-lg font-extrabold tracking-tight">{s.n}. {s.title}</h2>
              <div className="mt-2 text-sm leading-relaxed text-foreground/85 space-y-2">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-border">
          <PublicFooter tone="dark" />
        </div>
      </main>
    </div>
  );
}
